/**
 * AssetCard — Video-game style unit card with silhouette, bars, and threat level.
 */

import type { AssetSummary, Domain, FuelState, AssetLoadout } from '../../../types'
import { DOMAIN_CONFIG, TEMPO_CONFIG, type Tempo } from '../../../types'
import { AssetSilhouette } from '../../shared/AssetSilhouette'

interface AssetCardProps {
  asset: AssetSummary
  fuel?: FuelState
  loadout?: AssetLoadout
  onSelect: (assetId: string) => void
}

function domainColor(domain: string): string {
  return DOMAIN_CONFIG[domain as Domain]?.color ?? '#6366f1'
}

// ===== MC Rate Bar (10-seg) =====
function MCRateBar({ value, goal }: { value: number; goal: number }) {
  const pct = Math.round(value * 100)
  const isBelowGoal = value < goal
  const isCritical = value < 0.5
  const barColor = isCritical ? '#ef4444' : isBelowGoal ? '#f59e0b' : '#22c55e'
  const filled = Math.round(value * 10)

  return (
    <div className={`flex items-center justify-between gap-1 ${isCritical ? 'mc-critical' : ''}`}>
      <span className="text-[9px] text-term-textDim w-7 shrink-0">MC</span>
      <div className="flex gap-[1px] flex-1">
        {Array.from({ length: 10 }, (_, i) => (
          <div key={i} className="flex-1 h-2 rounded-[1px]" style={{ backgroundColor: i < filled ? barColor : '#3a3a5c40' }} />
        ))}
      </div>
      <span className="text-[9px] tabular-nums w-7 text-right" style={{ color: barColor }}>{pct}%</span>
      <span className="text-[8px] text-term-textDim/50 w-8 text-right">← {Math.round(goal * 100)}</span>
    </div>
  )
}

// ===== Fuel Bar (10-seg) =====
function FuelBar({ pct, bingoHrs }: { pct: number; bingoHrs: number }) {
  const filled = Math.round(pct * 10)
  const color = pct >= 0.60 ? '#22c55e' : pct >= 0.30 ? '#f59e0b' : '#ef4444'

  return (
    <div className="flex items-center justify-between gap-1">
      <span className="text-[9px] text-term-textDim w-7 shrink-0">FUEL</span>
      <div className="flex gap-[1px] flex-1">
        {Array.from({ length: 10 }, (_, i) => (
          <div key={i} className="flex-1 h-2 rounded-[1px]" style={{ backgroundColor: i < filled ? color : '#3a3a5c40' }} />
        ))}
      </div>
      <span className="text-[9px] tabular-nums w-7 text-right" style={{ color }}>{Math.round(pct * 100)}%</span>
      <span className="text-[8px] text-term-textDim/50 w-8 text-right">{bingoHrs.toFixed(1)}h</span>
    </div>
  )
}

// ===== Ordnance Dots =====
function OrdnanceDots({ used, total }: { used: number; total: number }) {
  const color = total > 0 ? (used / total > 0.7 ? '#22c55e' : used / total > 0.4 ? '#f59e0b' : '#ef4444') : '#6b7280'

  return (
    <div className="flex items-center justify-between gap-1">
      <span className="text-[9px] text-term-textDim w-7 shrink-0">ORD</span>
      <div className="flex gap-[2px] flex-1">
        {Array.from({ length: total }, (_, i) => (
          <div
            key={i}
            className="w-[6px] h-[6px] rounded-full"
            style={{ backgroundColor: i < used ? color : '#3a3a5c40' }}
          />
        ))}
      </div>
      <span className="text-[9px] tabular-nums text-term-textDim text-right w-[40px]">{used}/{total}</span>
    </div>
  )
}

// ===== Readiness Bar (10-seg, 0-100) =====
function ReadinessBar({ score }: { score: number }) {
  const filled = Math.round(score / 10)
  const color = score >= 75 ? '#22c55e' : score >= 50 ? '#f59e0b' : '#ef4444'

  return (
    <div className="flex items-center justify-between gap-1">
      <span className="text-[9px] text-term-textDim w-7 shrink-0">RDY</span>
      <div className="flex gap-[1px] flex-1">
        {Array.from({ length: 10 }, (_, i) => (
          <div key={i} className="flex-1 h-2 rounded-[1px]" style={{ backgroundColor: i < filled ? color : '#3a3a5c40' }} />
        ))}
      </div>
      <span className="text-[9px] tabular-nums w-16 text-right" style={{ color }}>{score.toFixed(0)}</span>
    </div>
  )
}

// ===== Threat Bar (5-seg, 0.0-1.0) =====
function ThreatBar({ exposure }: { exposure: number }) {
  const filled = Math.round(exposure * 5)
  const level = exposure >= 0.7 ? 'RED' : exposure >= 0.3 ? 'AMBER' : 'GREEN'
  const color = level === 'RED' ? '#ef4444' : level === 'AMBER' ? '#f59e0b' : '#22c55e'

  return (
    <div className="flex items-center justify-between gap-1">
      <span className="text-[9px] text-term-textDim w-7 shrink-0">THRT</span>
      <div className="flex gap-[2px] flex-1">
        {Array.from({ length: 5 }, (_, i) => (
          <div key={i} className="flex-1 h-2.5 rounded-[1px]" style={{ backgroundColor: i < filled ? color : '#3a3a5c40' }} />
        ))}
      </div>
      <span className="text-[9px] tabular-nums w-7 text-right" style={{ color }}>{exposure.toFixed(2)}</span>
      <span className="text-[8px] font-bold w-10 text-right" style={{ color }}>{level}</span>
    </div>
  )
}

export function AssetCard({ asset, fuel, loadout, onSelect }: AssetCardProps) {
  const dColor = domainColor(asset.domain)
  const isCritical = asset.mc_rate < 0.5
  const tempoConf = TEMPO_CONFIG[asset.current_tempo as Tempo]

  return (
    <div
      onClick={() => onSelect(asset.id)}
      className={`term-box p-3 cursor-pointer transition-all duration-150
                  hover:brightness-110 hover:scale-[1.01]
                  ${isCritical ? 'glow-error mc-critical' : ''}`}
      style={{
        borderColor: `${dColor}40`,
        minWidth: 220,
      }}
    >
      {/* Top row: domain badge + tempo badge */}
      <div className="flex items-center justify-between mb-2">
        <span
          className="text-[9px] font-bold px-1.5 py-0.5 rounded uppercase"
          style={{ backgroundColor: `${dColor}20`, color: dColor, border: `1px solid ${dColor}40` }}
        >
          {asset.domain}
        </span>
        {tempoConf && (
          <span
            className="text-[9px] font-medium px-1.5 py-0.5 rounded"
            style={{
              backgroundColor: `${tempoConf.color}20`,
              color: tempoConf.color,
              border: `1px solid ${tempoConf.color}40`,
            }}
          >
            {asset.current_tempo.replace(/_/g, ' ')}
          </span>
        )}
      </div>

      {/* Silhouette with radial glow */}
      <div
        className="flex items-center justify-center rounded py-1 mb-2"
        style={{
          background: `radial-gradient(ellipse at center, ${dColor}15 0%, transparent 70%)`,
        }}
      >
        <AssetSilhouette assetType={asset.asset_type} width={80} height={32} color={dColor} />
      </div>

      {/* Name + count */}
      <div className="flex items-baseline justify-between mb-0.5">
        <span className="text-xs font-bold text-term-text truncate">{asset.asset_type}</span>
        <span className="text-xs font-bold text-term-textDim tabular-nums ml-1 shrink-0">x{asset.assigned_count}</span>
      </div>

      {/* Role */}
      <div className="text-[9px] text-term-textDim mb-2 truncate">{asset.role}</div>

      {/* Divider */}
      <div className="border-t border-term-border/50 mb-2" />

      {/* MC Rate bar */}
      <MCRateBar value={asset.mc_rate} goal={asset.mc_rate_goal} />

      {/* Fuel bar — only for fueled assets */}
      {fuel && (
        <div className="mt-1">
          <FuelBar pct={fuel.fuel_pct} bingoHrs={fuel.time_to_bingo_hrs} />
        </div>
      )}

      {/* Ordnance dots — only when stations > 0 */}
      {loadout && loadout.stations_total > 0 && (
        <div className="mt-1">
          <OrdnanceDots used={loadout.stations_used} total={loadout.stations_total} />
        </div>
      )}

      {/* Readiness bar */}
      <div className="mt-1">
        <ReadinessBar score={asset.readiness_score} />
      </div>

      {/* Divider */}
      <div className="border-t border-term-border/50 my-2" />

      {/* Threat bar */}
      <ThreatBar exposure={asset.threat_exposure} />
    </div>
  )
}
